import { useState, useMemo } from 'react';
import { Plus, X, Trash2, AlertTriangle } from 'lucide-react';
import { format as formatDate } from 'date-fns';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useScheduleBlocks,
  useCreateScheduleBlock,
  useUpdateScheduleBlock,
  useDeleteScheduleBlock,
  useCheckOverlaps,
} from '@/hooks/use-schedule-blocks';
import { useProviders } from '@/hooks/use-providers';
import { useLocations } from '@/hooks/use-locations';
import { BLOCK_TYPE, BLOCK_TYPE_LABELS, type ScheduleBlockResponse } from '@vagusx/clients';

// ── Constants ─────────────────────────────────────────────────────────────────

const BLOCK_TYPE_COLORS: Record<number, string> = {
  [BLOCK_TYPE.PUBLIC_HOLIDAY]: 'text-red-600 bg-red-50 border-red-200',
  [BLOCK_TYPE.CONFERENCE]: 'text-blue-600 bg-blue-50 border-blue-200',
  [BLOCK_TYPE.PROVIDER_UNAVAILABLE]: 'text-orange-600 bg-orange-50 border-orange-200',
  [BLOCK_TYPE.STAFF_MEETING]: 'text-purple-600 bg-purple-50 border-purple-200',
  [BLOCK_TYPE.VACATION]: 'text-green-600 bg-green-50 border-green-200',
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function ScheduleBlocksPanel(): React.ReactElement {
  const { data: blocks = [], isLoading } = useScheduleBlocks();
  const providersQuery = useProviders();
  const locationsQuery = useLocations();
  const providersList = providersQuery.data?.items ?? [];
  const locationsList = locationsQuery.data?.items ?? [];
  const createBlock = useCreateScheduleBlock();
  const updateBlock = useUpdateScheduleBlock();
  const deleteBlock = useDeleteScheduleBlock();
  const checkOverlaps = useCheckOverlaps();

  const [isCreating, setIsCreating] = useState(false);
  const [editingBlock, setEditingBlock] = useState<ScheduleBlockResponse | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<ScheduleBlockResponse | null>(null);
  const [overlapWarning, setOverlapWarning] = useState<{
    appointmentCount: number;
    overlappingBlocks: Array<{ title: string }>;
  } | null>(null);
  const [pendingCreate, setPendingCreate] = useState<{
    data: Parameters<typeof createBlock.mutate>[0];
  } | null>(null);

  // Form state
  const [formProvider, setFormProvider] = useState('');
  const [formLocation, setFormLocation] = useState('__all__');
  const [formBlockType, setFormBlockType] = useState(String(BLOCK_TYPE.VACATION));
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formStartDate, setFormStartDate] = useState('');
  const [formStartTime, setFormStartTime] = useState('09:00');
  const [formEndDate, setFormEndDate] = useState('');
  const [formEndTime, setFormEndTime] = useState('17:00');
  const [formIsFullDay, setFormIsFullDay] = useState(false);

  const resetForm = (): void => {
    setFormProvider('');
    setFormLocation('__all__');
    setFormBlockType(String(BLOCK_TYPE.VACATION));
    setFormTitle('');
    setFormDescription('');
    setFormStartDate('');
    setFormStartTime('09:00');
    setFormEndDate('');
    setFormEndTime('17:00');
    setFormIsFullDay(false);
  };

  const openCreate = (): void => {
    resetForm();
    setEditingBlock(null);
    setIsCreating(true);
  };

  const openEdit = (block: ScheduleBlockResponse): void => {
    setEditingBlock(block);
    setIsCreating(false);
    setFormProvider(block.providerId);
    setFormLocation(block.locationId ?? '__all__');
    setFormBlockType(String(block.blockType));
    setFormTitle(block.title);
    setFormDescription(block.description ?? '');
    const start = new Date(block.startTime);
    const end = new Date(block.endTime);
    setFormStartDate(formatDate(start, 'yyyy-MM-dd'));
    setFormStartTime(formatDate(start, 'HH:mm'));
    setFormEndDate(formatDate(end, 'yyyy-MM-dd'));
    setFormEndTime(formatDate(end, 'HH:mm'));
    setFormIsFullDay(block.isFullDay);
  };

  const buildDatetime = (date: string, time: string, isEnd: boolean): string => {
    if (formIsFullDay) {
      // Create local midnight/end-of-day and convert to UTC
      const localDate = isEnd ? new Date(`${date}T23:59:59.999`) : new Date(`${date}T00:00:00.000`);
      return localDate.toISOString();
    }
    // Create local time and convert to UTC
    const localDate = new Date(`${date}T${time}`);
    return localDate.toISOString();
  };

  const handleSubmitCreate = (): void => {
    const startTime = buildDatetime(formStartDate, formStartTime, false);
    const endTime = buildDatetime(formEndDate || formStartDate, formEndTime, true);
    const createData = {
      providerId: formProvider,
      locationId: formLocation === '__all__' ? undefined : formLocation || undefined,
      blockType: Number(formBlockType),
      title: formTitle,
      description: formDescription || undefined,
      startTime,
      endTime,
      isFullDay: formIsFullDay,
      markAppointmentsRescheduling: false,
    };

    // Check for overlaps first
    checkOverlaps.mutate(
      {
        providerId: formProvider,
        startTime,
        endTime,
        locationId: formLocation === '__all__' ? undefined : formLocation || undefined,
      },
      {
        onSuccess: (result) => {
          const hasAppointments = result.overlappingAppointments.count > 0;
          const hasBlocks = result.overlappingBlocks.length > 0;

          if (hasAppointments || hasBlocks) {
            setOverlapWarning({
              appointmentCount: result.overlappingAppointments.count,
              overlappingBlocks: result.overlappingBlocks,
            });
            setPendingCreate({ data: createData });
          } else {
            createBlock.mutate(createData, {
              onSuccess: () => {
                setIsCreating(false);
                resetForm();
              },
            });
          }
        },
      }
    );
  };

  const confirmCreate = (markRescheduling: boolean): void => {
    if (!pendingCreate) return;
    createBlock.mutate(
      { ...pendingCreate.data, markAppointmentsRescheduling: markRescheduling },
      {
        onSuccess: () => {
          setIsCreating(false);
          setOverlapWarning(null);
          setPendingCreate(null);
          resetForm();
        },
      }
    );
  };

  const handleSubmitUpdate = (): void => {
    if (!editingBlock) return;
    updateBlock.mutate(
      {
        id: editingBlock.id,
        data: {
          title: formTitle,
          description: formDescription || null,
          blockType: Number(formBlockType),
        },
      },
      {
        onSuccess: () => setEditingBlock(null),
      }
    );
  };

  const handleDelete = (): void => {
    if (!deleteConfirm) return;
    deleteBlock.mutate(deleteConfirm.id, {
      onSuccess: () => {
        setDeleteConfirm(null);
        if (editingBlock?.id === deleteConfirm.id) setEditingBlock(null);
      },
    });
  };

  // Group blocks by upcoming / past
  const now = new Date();
  const { upcoming, past } = useMemo(() => {
    const u: ScheduleBlockResponse[] = [];
    const p: ScheduleBlockResponse[] = [];
    for (const block of blocks) {
      if (new Date(block.endTime) > now) u.push(block);
      else p.push(block);
    }
    return { upcoming: u, past: p };
  }, [blocks]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatBlockDates = (block: ScheduleBlockResponse): string => {
    const start = new Date(block.startTime);
    const end = new Date(block.endTime);
    if (block.isFullDay) {
      const startStr = formatDate(start, 'MMM d, yyyy');
      const endStr = formatDate(end, 'MMM d, yyyy');
      return startStr === endStr ? startStr : `${startStr} - ${endStr}`;
    }
    const startStr = formatDate(start, 'MMM d, yyyy h:mm a');
    const endStr = formatDate(end, 'h:mm a');
    return `${startStr} - ${endStr}`;
  };

  const showForm = isCreating || editingBlock;
  const isSubmitting = createBlock.isPending || checkOverlaps.isPending;

  return (
    <div className="flex h-full">
      {/* Block List */}
      <div className={cn('border-r flex flex-col shrink-0', showForm ? 'w-72' : 'flex-1')}>
        <div className="p-4 flex items-center justify-between border-b shrink-0">
          <h3 className="font-semibold">Schedule Blocks</h3>
          <Button size="sm" onClick={openCreate}>
            <Plus className="h-4 w-4 mr-1" />
            Add Block
          </Button>
        </div>
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            {isLoading && (
              <div className="p-4 text-sm text-muted-foreground text-center">Loading...</div>
            )}
            {!isLoading && blocks.length === 0 && (
              <div className="p-8 text-sm text-muted-foreground text-center">
                No schedule blocks. Create one to block off provider time.
              </div>
            )}
            {upcoming.length > 0 && (
              <>
                <div className="px-2 pt-2 pb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Upcoming
                </div>
                {upcoming.map((block) => (
                  <button
                    key={block.id}
                    onClick={() => openEdit(block)}
                    className={cn(
                      'w-full rounded-lg px-3 py-2.5 text-left text-sm transition-colors',
                      'hover:bg-accent hover:text-accent-foreground',
                      editingBlock?.id === block.id && 'bg-accent text-accent-foreground'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] shrink-0',
                          BLOCK_TYPE_COLORS[block.blockType] ?? ''
                        )}
                      >
                        {BLOCK_TYPE_LABELS[block.blockType] ?? 'Block'}
                      </Badge>
                      <span className="font-medium truncate">{block.title}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <span>
                        {block.provider.firstName} {block.provider.lastName}
                      </span>
                      <span>{block.location ? block.location.name : 'All locations'}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {formatBlockDates(block)}
                    </div>
                  </button>
                ))}
              </>
            )}
            {past.length > 0 && (
              <>
                <div className="px-2 pt-3 pb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Past
                </div>
                {past.map((block) => (
                  <button
                    key={block.id}
                    onClick={() => openEdit(block)}
                    className={cn(
                      'w-full rounded-lg px-3 py-2.5 text-left text-sm transition-colors opacity-60',
                      'hover:bg-accent hover:text-accent-foreground hover:opacity-100',
                      editingBlock?.id === block.id &&
                        'bg-accent text-accent-foreground opacity-100'
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px] shrink-0',
                          BLOCK_TYPE_COLORS[block.blockType] ?? ''
                        )}
                      >
                        {BLOCK_TYPE_LABELS[block.blockType] ?? 'Block'}
                      </Badge>
                      <span className="font-medium truncate">{block.title}</span>
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">
                      {block.provider.firstName} {block.provider.lastName}
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="flex-1 overflow-y-auto min-w-0 min-h-0">
          <div className="p-4 border-b sticky top-0 bg-background z-10 flex items-center justify-between">
            <h3 className="font-semibold">
              {isCreating ? 'Create Schedule Block' : 'Edit Schedule Block'}
            </h3>
            <div className="flex items-center gap-2">
              {editingBlock && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-muted-foreground hover:text-destructive"
                  onClick={() => setDeleteConfirm(editingBlock)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => {
                  setIsCreating(false);
                  setEditingBlock(null);
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="p-4 space-y-4">
            {/* Provider */}
            <div className="space-y-2">
              <Label htmlFor="block-provider">
                Provider <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formProvider}
                onValueChange={setFormProvider}
                disabled={!!editingBlock}
              >
                <SelectTrigger id="block-provider">
                  <SelectValue placeholder="Select provider" />
                </SelectTrigger>
                <SelectContent>
                  {providersList.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.firstName} {p.lastName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div className="space-y-2">
              <Label htmlFor="block-location">Location (optional)</Label>
              <Select
                value={formLocation}
                onValueChange={setFormLocation}
                disabled={!!editingBlock}
              >
                <SelectTrigger id="block-location">
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All locations</SelectItem>
                  {locationsList.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Block Type */}
            <div className="space-y-2">
              <Label htmlFor="block-type">
                Type <span className="text-destructive">*</span>
              </Label>
              <Select value={formBlockType} onValueChange={setFormBlockType}>
                <SelectTrigger id="block-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(BLOCK_TYPE).map(([key, value]) => (
                    <SelectItem key={key} value={String(value)}>
                      {BLOCK_TYPE_LABELS[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div className="space-y-2">
              <Label htmlFor="block-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="block-title"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                placeholder="e.g., Spring Break, Medical Conference"
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="block-description">Description (optional)</Label>
              <Textarea
                id="block-description"
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                rows={2}
              />
            </div>

            <Separator />

            {/* Full Day Toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="block-fullday">Full day</Label>
              <Switch
                id="block-fullday"
                checked={formIsFullDay}
                onCheckedChange={setFormIsFullDay}
                disabled={!!editingBlock}
              />
            </div>

            {/* Date/Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="block-start-date">
                  Start date <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="block-start-date"
                  type="date"
                  value={formStartDate}
                  onChange={(e) => setFormStartDate(e.target.value)}
                  disabled={!!editingBlock}
                />
              </div>
              {!formIsFullDay && (
                <div className="space-y-2">
                  <Label htmlFor="block-start-time">Start time</Label>
                  <Input
                    id="block-start-time"
                    type="time"
                    value={formStartTime}
                    onChange={(e) => setFormStartTime(e.target.value)}
                    disabled={!!editingBlock}
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="block-end-date">End date</Label>
                <Input
                  id="block-end-date"
                  type="date"
                  value={formEndDate || formStartDate}
                  onChange={(e) => setFormEndDate(e.target.value)}
                  disabled={!!editingBlock}
                />
              </div>
              {!formIsFullDay && (
                <div className="space-y-2">
                  <Label htmlFor="block-end-time">End time</Label>
                  <Input
                    id="block-end-time"
                    type="time"
                    value={formEndTime}
                    onChange={(e) => setFormEndTime(e.target.value)}
                    disabled={!!editingBlock}
                  />
                </div>
              )}
            </div>

            <Separator />

            {/* Actions */}
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setIsCreating(false);
                  setEditingBlock(null);
                }}
              >
                Cancel
              </Button>
              {isCreating ? (
                <Button
                  onClick={handleSubmitCreate}
                  disabled={!formProvider || !formTitle || !formStartDate || isSubmitting}
                >
                  {isSubmitting ? 'Creating...' : 'Create Block'}
                </Button>
              ) : (
                <Button onClick={handleSubmitUpdate} disabled={!formTitle || updateBlock.isPending}>
                  {updateBlock.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overlap Warning Dialog */}
      <Dialog open={!!overlapWarning} onOpenChange={(open) => !open && setOverlapWarning(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Scheduling Conflicts Found
            </DialogTitle>
            <DialogDescription>
              {overlapWarning?.appointmentCount
                ? `${overlapWarning.appointmentCount} appointment(s) overlap with this block.`
                : ''}
              {overlapWarning?.overlappingBlocks.length
                ? ` ${overlapWarning.overlappingBlocks.length} existing block(s) also overlap.`
                : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {overlapWarning && overlapWarning.appointmentCount > 0 && (
              <p className="text-sm">
                Would you like to mark the {overlapWarning.appointmentCount} overlapping
                appointment(s) as &quot;Rescheduling&quot;?
              </p>
            )}
            {overlapWarning?.overlappingBlocks.map((b, i) => (
              <div key={i} className="text-sm text-muted-foreground">
                Overlaps with: {b.title}
              </div>
            ))}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => confirmCreate(false)}>
              Create without rescheduling
            </Button>
            {overlapWarning && overlapWarning.appointmentCount > 0 && (
              <Button onClick={() => confirmCreate(true)}>
                Create &amp; mark for rescheduling
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Schedule Block</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &quot;{deleteConfirm?.title}&quot;? This will not
              revert any appointments already marked for rescheduling.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteBlock.isPending}>
              {deleteBlock.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
